import React, { useEffect, useMemo, useRef, useState } from 'react'
import { ArchiveMorphologyStore } from '../ArchiveMorphologyStore';
import { handleInput, moveCursorToFirstOpenSlot, updateScrollPositions, parseClueWithItalics } from '../archiveMorphologyUtils';
import { useWindowHeightStyle } from '../../shared/utils';
import MobileToolbar from './MobileToolbar';
import SolutionInput from './SolutionInput';
import Keyboard from '../../shared/Keyboard';
import PauseCover from '../../shared/PauseCover';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { IconButton } from '@mui/material';
import MobileMorphologyInstructions from './MobileInstructions';
import MobileMorphologyAbout from './MobileAbout';
import AlmostThereDialog from './AlmostThereDialog';
import '../../Morphology/MobileMorphology.less';

let lastScrollTime = 0;
let lastScrollSwitchId = 0;

const keyboardHeight = 192;
const upDownButtonsHeight = 30;
const menuBarHeight = 40;

const MobileMorphology = () => {
  const { clues, author, guesses, completed, focusedClueIndex } = ArchiveMorphologyStore.useState();

  // Parse clues once when clues change (only happens on initial load)
  const parsedClues = useMemo(() => {
    return clues.map(clue => parseClueWithItalics(clue.clue));
  }, [clues]);

  const [initialScroll, setInitialScroll] = useState(0);

  const totalHeight = useWindowHeightStyle();
  const remainingHeight = completed ? totalHeight - menuBarHeight : totalHeight - (keyboardHeight + menuBarHeight + upDownButtonsHeight);
  const scrollableContainerHeight = remainingHeight / 2;

  const inputsRef = useRef<HTMLDivElement>(null);
  const cluesRef = useRef<HTMLDivElement>(null);

  // handle scrolling things into view when using up/down button navigation
  useEffect(() => {
    // the setTimeout avoids an annoying issue where you tap a row at the top or bottom edge
    // of the scroll container and it immediately jumps and forces you to select one past
    // what you were aiming for
    setTimeout(() => {
      updateScrollPositions(focusedClueIndex);
    }, 10);
  }, [focusedClueIndex]);

  // switcher is just a super arbitrary parameter used to tell which
  // container is being scrolled so we can match the scroll on the other one
  const syncScrolls = (e: React.UIEvent, switcher: number) => {
    if (!inputsRef.current || !cluesRef.current) return;

    // When you scroll one container manually, the triggered scroll on the other still invokes
    // the onScroll event and consequently triggers some kind of feedback loop.
    // This logic basically says "ignore any scroll events that are targeting a new
    // container within less than a 50ms interval" because those can be assumed to be
    // the undesirable false triggers.
    if (switcher === lastScrollSwitchId) {
      lastScrollTime = e.timeStamp;
    } else if (e.timeStamp - lastScrollTime > 50) {
      lastScrollTime = e.timeStamp;
      lastScrollSwitchId = switcher;
    } else {
      return;
    }

    const inputs = inputsRef.current;
    const clues = cluesRef.current;

    const inputsScrollHeight = inputs.scrollHeight - inputs.clientHeight;
    const cluesScrollHeight = clues.scrollHeight - clues.clientHeight;

    if (switcher === 1) {
      const scrollPercent = (inputs.scrollTop / inputsScrollHeight) * 100;
      clues.scrollTop = (scrollPercent / 100) * cluesScrollHeight;
    } else {
      const scrollPercent = (clues.scrollTop / cluesScrollHeight) * 100;
      inputs.scrollTop = (scrollPercent / 100) * inputsScrollHeight;
    }
  };

  const onNext = () => {
    ArchiveMorphologyStore.update(s => {
      const isOnLastClue = s.focusedClueIndex === clues.length - 1;
      s.focusedClueIndex = isOnLastClue ? 0 : s.focusedClueIndex + 1;
      moveCursorToFirstOpenSlot(s);
    });
  };

  const onPrevious = () => {
    ArchiveMorphologyStore.update(s => {
      const isOnFirstClue = s.focusedClueIndex === 0;
      s.focusedClueIndex = isOnFirstClue ? clues.length - 1 : s.focusedClueIndex - 1;
      moveCursorToFirstOpenSlot(s);
    });
  };

  return (
    <>
      <MobileToolbar/>
      <AlmostThereDialog/>
      <div className="morphologyContainer mobile">
        <div className="inputsContainer" ref={inputsRef} onScroll={(e) => syncScrolls(e, 1)} style={{ height: scrollableContainerHeight }}>
          {clues.map((clue, i) => (
            <div
              className={`inputRow ${focusedClueIndex === i ? 'selected' : ''}`}
              key={i}
              onClick={(e: any) => {
                if (e.__isSelectingIndividualCell) return;
                // (We need both onClick and onScroll because MobileMorphology is also
                // used for small window sizes on desktop.)
                // this onClick is for when they click the row but not a specific cell in it
                ArchiveMorphologyStore.update(s => {
                  s.focusedClueIndex = i;
                  moveCursorToFirstOpenSlot(s);
                });
              }}
              onTouchStart={() => {
                const scrollTop = document.querySelector('.inputsContainer')!.scrollTop;
                setInitialScroll(scrollTop);
              }}
              onTouchEnd={(e: any) => {
                if (e.__isSelectingIndividualCell) return;
                const scrollTop = document.querySelector('.inputsContainer')!.scrollTop;
                if (scrollTop !== initialScroll) return;
                // this onClick is for when they click the row but not a specific cell in it
                ArchiveMorphologyStore.update(s => {
                  s.focusedClueIndex = i;
                  moveCursorToFirstOpenSlot(s);
                });
              }}
            >
              <div className="clueIndex">{i+1}</div>
              <div className="solutionInputContainer">
                <SolutionInput
                  clueIndex={i}
                  guess={guesses[i]}
                />
              </div>
              <div className="morph">{clue.morph || ''}</div>
            </div>
          ))}
        </div>
        <div className="cluesContainer" ref={cluesRef} onScroll={(e) => syncScrolls(e, 2)} style={{ height: scrollableContainerHeight }}>
          {clues.map((clue, i) => (
            <div
              className={`clueRow ${focusedClueIndex === i ? 'selected' : ''}`}
              key={i}
              onClick={() => {
                // (We need both onClick and onScroll because MobileMorphology is also
                // used for small window sizes on desktop.)
                // this onClick is for when they click the row but not a specific cell in it
                ArchiveMorphologyStore.update(s => {
                  s.focusedClueIndex = i;
                  moveCursorToFirstOpenSlot(s);
                });
              }}
              onTouchStart={() => {
                const scrollTop = document.querySelector('.inputsContainer')!.scrollTop;
                setInitialScroll(scrollTop);
              }}
              onTouchEnd={() => {
                const scrollTop = document.querySelector('.inputsContainer')!.scrollTop;
                if (scrollTop !== initialScroll) return;
                // this onClick is for when they click the row but not a specific cell in it
                ArchiveMorphologyStore.update(s => {
                  s.focusedClueIndex = i;
                  moveCursorToFirstOpenSlot(s);
                });
              }}
            >
              <div className="clueContainer">
                <div className="clueIndex">{i+1}</div>
                <div className="clueText">
                  {parsedClues[i]}
                </div>
              </div>
            </div>
          ))}
        </div>
        {!completed && <div className="navigationContainer">
          <IconButton onClick={onPrevious} disableRipple disableFocusRipple disableTouchRipple>
            <KeyboardArrowUpIcon/>
          </IconButton>
          <IconButton onClick={onNext} disableRipple disableFocusRipple disableTouchRipple>
            <KeyboardArrowDownIcon/>
          </IconButton>
        </div>}
        {!completed && <Keyboard handleInput={handleInput}/>}
        <PauseCover store={ArchiveMorphologyStore as any} useCompleted={true} completed={completed}/>
        <MobileMorphologyInstructions/>
        <MobileMorphologyAbout/>
      </div>
    </>
  );
};

export default MobileMorphology;